import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import { useStore } from '../../context/StoreContext';
import { FiSave, FiSettings, FiTruck, FiTrash2, FiShield, FiCreditCard, FiActivity, FiMail, FiFileText, FiLayout, FiImage, FiClock, FiEye, FiEyeOff } from 'react-icons/fi';
import AdminPayments from './AdminPayments';
import AdminPaymentDiagnostics from './AdminPaymentDiagnostics';
import AdminEmails from './AdminEmails';
import AdminPages from './AdminPages';
import AdminLanding from './AdminLanding';
import SafeIcon from '../../common/SafeIcon';

const DEFAULT_LOADING_ICON = 'https://cloud.sharemyimage.com/2026/08/05/whitemotionvid-837d33ed-2540-4e3f-ae05-b9b121fb299c.gif';

// Dummy UUID that will never match - used to satisfy Supabase's WHERE clause requirement
const DUMMY_UUID = '00000000-0000-0000-0000-000000000000';

const AdminSettings = () => {
  const { appSettings, fetchAppSettings } = useStore();
  const [resetting, setResetting] = useState(false);
  const [activeTab, setActiveTab] = useState('store');
  const [loadingIconUrl, setLoadingIconUrl] = useState('');
  const [savingIcon, setSavingIcon] = useState(false);
  const [iconSaved, setIconSaved] = useState(false);
  const [comingSoon, setComingSoon] = useState(false);
  const [savingComingSoon, setSavingComingSoon] = useState(false);
  const [comingSoonSaved, setComingSoonSaved] = useState(false);

  useEffect(() => {
    const setting = appSettings?.find(s => s.key === 'loading_icon_url');
    setLoadingIconUrl(setting?.value || DEFAULT_LOADING_ICON);

    const csSetting = appSettings?.find(s => s.key === 'shop_coming_soon');
    setComingSoon(csSetting?.value === true || csSetting?.value === 'true');
  }, [appSettings]);

  const handleSaveLoadingIcon = async () => {
    setSavingIcon(true);
    setIconSaved(false);
    try {
      const { error } = await supabase
        .from('app_settings')
        .upsert({ key: 'loading_icon_url', value: loadingIconUrl, updated_at: new Date().toISOString() }, { onConflict: 'key' });
      
      if (error) throw error;
      await fetchAppSettings();
      setIconSaved(true);
      setTimeout(() => setIconSaved(false), 3000);
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSavingIcon(false);
    }
  };

  const handleToggleComingSoon = async () => {
    setSavingComingSoon(true);
    setComingSoonSaved(false);
    const newValue = !comingSoon;
    try {
      const { error } = await supabase
        .from('app_settings')
        .upsert({ key: 'shop_coming_soon', value: String(newValue), updated_at: new Date().toISOString() }, { onConflict: 'key' });
      
      if (error) throw error;
      setComingSoon(newValue);
      await fetchAppSettings();
      setComingSoonSaved(true);
      setTimeout(() => setComingSoonSaved(false), 3000);
    } catch (err) {
      alert('Failed to save: ' + err.message);
    } finally {
      setSavingComingSoon(false);
    }
  };

  const safeDeleteAll = async (tableName) => {
    try {
      const { error } = await supabase
        .from(tableName)
        .delete()
        .neq('id', DUMMY_UUID);
      if (error && error.code !== '42P01') {
        console.warn(`Warning deleting from ${tableName}:`, error.message);
      }
      return true;
    } catch (err) {
      console.warn(`Warning deleting from ${tableName}:`, err.message);
      return false;
    }
  };

  const safeResetColumn = async (tableName, column, value) => {
    try {
      const { error } = await supabase
        .from(tableName)
        .update({ [column]: value })
        .neq('id', DUMMY_UUID);
      if (error && error.code !== '42P01') {
        console.warn(`Warning updating ${tableName}:`, error.message);
      }
      return true;
    } catch (err) {
      console.warn(`Warning updating ${tableName}:`, err.message);
      return false;
    }
  };

  const handleResetData = async () => {
    if (window.confirm('Are you sure you want to reset all order and transaction data? Products, customers, and settings will remain safe.')) {
      setResetting(true);
      try {
        // First try the RPC function
        const { error: rpcError } = await supabase.rpc('reset_test_data');
        
        if (rpcError) {
          console.warn('RPC failed, falling back to direct deletion:', rpcError.message);
          
          // Fallback: delete directly from each table using Supabase client
          // Order matters due to foreign key constraints
          const tablesToDelete = [
            'payment_logs',
            'payment_webhooks', 
            'payment_transactions',
            'order_items',
            'orders',
            'analytics_events',
            'waitlist'
          ];

          for (const table of tablesToDelete) {
            await safeDeleteAll(table);
          }

          // Reset promo code usage counts
          await safeResetColumn('promo_codes', 'uses_count', 0);
        }
        
        alert('Order and transaction data has been successfully cleared.');
        window.location.reload();
      } catch (err) {
        console.error('Reset data error:', err);
        alert('Error resetting data: ' + err.message + '\n\nIf this persists, go to Supabase Dashboard → SQL Editor and run the latest migration files manually.');
      } finally {
        setResetting(false);
      }
    }
  };

  const renderContent = () => {
    switch(activeTab) {
      case 'payments':
        return <AdminPayments />;
      case 'diagnostics':
        return <AdminPaymentDiagnostics />;
      case 'emails':
        return <AdminEmails />;
      case 'pages':
        return <AdminPages />;
      case 'landing':
        return <AdminLanding />;
      default:
        return (
          <div className="space-y-8 pb-12">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8 max-w-2xl">
              <div className="flex items-center gap-3 mb-6 border-b border-gray-100 pb-4">
                <SafeIcon icon={FiSettings} className="text-brand-gold w-6 h-6" />
                <h2 className="text-xl font-serif text-gray-800">Store Settings</h2>
              </div>

              <div className="bg-gray-50/50 p-5 md:p-6 rounded-xl border border-gray-100 mb-8">
                <p className="text-sm text-gray-600 leading-relaxed">
                  Shipping districts and delivery fees are managed in the <strong className="text-brand-darkSage">Shipping</strong> tab in the admin sidebar.
                </p>
              </div>

              {/* Coming Soon Toggle */}
              <div className="border-t border-gray-100 pt-8 mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <SafeIcon icon={FiClock} className="text-brand-gold w-5 h-5" />
                  <h3 className="font-semibold text-gray-800">Coming Soon Mode</h3>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  When enabled, customers visiting the shop will see a "Coming Soon" page with a waitlist signup form. 
                  They won't be able to browse products or place orders until you turn this off.
                </p>

                <div className={`p-5 rounded-xl border-2 transition-all ${
                  comingSoon 
                    ? 'bg-amber-50 border-amber-200' 
                    : 'bg-green-50 border-green-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        comingSoon ? 'bg-amber-100' : 'bg-green-100'
                      }`}>
                        <SafeIcon 
                          icon={comingSoon ? FiEyeOff : FiEye} 
                          className={comingSoon ? 'text-amber-600' : 'text-green-600'} 
                          size={20} 
                        />
                      </div>
                      <div>
                        <p className={`font-bold text-sm ${comingSoon ? 'text-amber-800' : 'text-green-800'}`}>
                          {comingSoon ? 'Shop is Hidden (Coming Soon)' : 'Shop is Live'}
                        </p>
                        <p className={`text-xs ${comingSoon ? 'text-amber-600' : 'text-green-600'}`}>
                          {comingSoon 
                            ? 'Customers see the Coming Soon page with waitlist' 
                            : 'Customers can browse and purchase products'}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleToggleComingSoon}
                      disabled={savingComingSoon}
                      className={`relative w-14 h-7 rounded-full transition-all duration-300 ${
                        comingSoon ? 'bg-amber-400' : 'bg-green-400'
                      } disabled:opacity-50`}
                    >
                      <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${
                        comingSoon ? 'translate-x-7' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>

                  {comingSoonSaved && (
                    <p className="text-xs font-bold text-green-600 mt-3">✓ Setting saved successfully</p>
                  )}
                </div>

                {comingSoon && (
                  <div className="mt-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                    <p className="text-xs text-blue-700 leading-relaxed">
                      <strong>💡 Tip:</strong> Manage waitlist subscribers and send announcement emails from the <strong>Waitlist</strong> tab in the admin sidebar.
                    </p>
                  </div>
                )}
              </div>

              {/* Loading Icon Setting */}
              <div className="border-t border-gray-100 pt-8 mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <SafeIcon icon={FiImage} className="text-brand-gold w-5 h-5" />
                  <h3 className="font-semibold text-gray-800">Loading Icon</h3>
                </div>
                <p className="text-xs text-gray-500 mb-4">
                  This animated icon is displayed while pages and data are loading across the site.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Icon URL (GIF or image)</label>
                    <input
                      type="url"
                      value={loadingIconUrl}
                      onChange={(e) => setLoadingIconUrl(e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-white focus:border-brand-gold focus:ring-1 focus:ring-brand-gold outline-none transition-all"
                      placeholder="https://example.com/loading.gif"
                    />
                  </div>
                  <div className="flex items-center gap-4">
                    <button
                      onClick={handleSaveLoadingIcon}
                      disabled={savingIcon || !loadingIconUrl.trim()}
                      className="bg-brand-darkSage text-white px-6 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-brand-gold transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      <SafeIcon icon={FiSave} size={14} />
                      {savingIcon ? 'Saving...' : 'Save'}
                    </button>
                    {iconSaved && (
                      <span className="text-xs font-bold text-green-600">✓ Saved successfully</span>
                    )}
                  </div>
                  {loadingIconUrl && (
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 flex items-center gap-4">
                      <img
                        src={loadingIconUrl}
                        alt="Loading icon preview"
                        className="w-16 h-16 object-contain rounded-lg border border-gray-200 bg-white p-1"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div>
                        <p className="text-xs font-bold text-gray-600">Preview</p>
                        <p className="text-[10px] text-gray-400 mt-0.5">Current loading icon</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Danger Zone */}
              <div className="bg-red-50/50 p-5 md:p-6 rounded-xl border border-red-100">
                <div className="flex items-center gap-2 mb-4">
                  <SafeIcon icon={FiTrash2} className="text-red-500 w-5 h-5" />
                  <h3 className="font-semibold text-red-800">Danger Zone</h3>
                </div>
                <div>
                  <p className="text-xs text-red-600 mb-5 max-w-md leading-relaxed">
                    Reset all test data. This will <strong className="font-bold">only remove orders, transactions, payment logs, webhooks, analytics events, and waitlist entries</strong>, and reset dashboard/report figures. Everything else (products, customers, settings, etc.) will remain exactly the same.
                  </p>
                  <button 
                    type="button" 
                    onClick={handleResetData}
                    disabled={resetting}
                    className="bg-red-500 text-white px-6 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-red-600 transition-colors disabled:opacity-50"
                  >
                    {resetting ? 'Resetting...' : 'Reset All Test Data'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col md:flex-row gap-8">
      <div className="w-full md:w-64 flex-shrink-0">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden sticky top-24">
          <div className="p-4 border-b border-gray-100 bg-gray-50/50">
            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500">Settings Menu</h3>
          </div>
          <div className="p-2 space-y-1">
            <button 
              onClick={() => setActiveTab('store')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                activeTab === 'store' ? 'bg-brand-darkSage text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={FiSettings} className={activeTab === 'store' ? 'text-brand-gold' : 'text-gray-400'} /> Store Settings
            </button>
            <button 
              onClick={() => setActiveTab('payments')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                activeTab === 'payments' ? 'bg-brand-darkSage text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={FiCreditCard} className={activeTab === 'payments' ? 'text-brand-gold' : 'text-gray-400'} /> Payments Config
            </button>
            <button 
              onClick={() => setActiveTab('diagnostics')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                activeTab === 'diagnostics' ? 'bg-brand-darkSage text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={FiActivity} className={activeTab === 'diagnostics' ? 'text-brand-gold' : 'text-gray-400'} /> Payment Diagnostics
            </button>
            <button 
              onClick={() => setActiveTab('emails')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                activeTab === 'emails' ? 'bg-brand-darkSage text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={FiMail} className={activeTab === 'emails' ? 'text-brand-gold' : 'text-gray-400'} /> Emails & Templates
            </button>
            <button
              onClick={() => setActiveTab('pages')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                activeTab === 'pages' ? 'bg-brand-darkSage text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={FiFileText} className={activeTab === 'pages' ? 'text-brand-gold' : 'text-gray-400'} /> Terms & Privacy
            </button>
            <button 
              onClick={() => setActiveTab('landing')}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-all ${
                activeTab === 'landing' ? 'bg-brand-darkSage text-white shadow-md' : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <SafeIcon icon={FiLayout} className={activeTab === 'landing' ? 'text-brand-gold' : 'text-gray-400'} /> Landing Content
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1">
        {renderContent()}
      </div>
    </div>
  );
};

export default AdminSettings;